export type OrgStatus =
  | 'ACTIVE'
  | 'DEACTIVATED'
  | 'SOURCE_COUNT_CAPPED'
  | 'SOURCE_DATA_CAPPED'
  | 'TRIAL_EXPIRED';

export type OrgMemberAccessRole = 'none' | 'member' | 'collaborator' | 'admin';

export interface OrgTier {
  id: number;
  name: string;
  display_name: string;
  record_count_limit: number;
  record_count_limit_time: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  data_source_count_limit: number;
  trial_period_days?: number;
  sandbox?: boolean;
  user_members_limit?: number;
  user_ttl_days?: number;
}

export interface OrgUserRef {
  id: number;
  full_name?: string;
  email: string;
}

export interface AdminOrg {
  id: number;
  name: string;
  description?: string | null;
  email_domain: string;
  email?: string | null;
  client_identifier?: string | null;
  org_webhook_host?: string | null;
  cluster_id?: number;
  cluster_status?: string;
  access_roles?: string[];
  owner?: OrgUserRef;
  billing_owner?: OrgUserRef;
  admins?: OrgUserRef[];
  org_tier: OrgTier;
  status: OrgStatus;
  self_signup: boolean;
  self_signup_members_limit?: number;
  trial_expires_at?: string | null;
  members_default_access_role?: string;
  enable_nexla_password_login?: boolean;
  email_domain_verified_at?: string | null;
  name_verified_at?: string | null;
  updated_at: string;
  created_at: string;
}

export interface OrgMember {
  id: number;
  full_name: string;
  email: string;
  'is_admin?': boolean;
  access_role?: OrgMemberAccessRole;
  access_role_expires_at?: string | null;
  org_membership_status: 'ACTIVE' | 'DEACTIVATED';
  user_status:
    | 'ACTIVE'
    | 'DEACTIVATED'
    | 'SOURCE_COUNT_CAPPED'
    | 'SOURCE_DATA_CAPPED'
    | 'TRIAL_EXPIRED';
}

export interface OrgAccountMetricPoint {
  time?: string;
  start_time: string;
  end_time: string;
  data: {
    records: number;
    size: number;
    pipelineCount?: number;
  };
}

export interface OrgAccountMetrics {
  status: number;
  metrics: OrgAccountMetricPoint[];
}

export interface UpdateOrgPayload {
  name?: string;
  description?: string;
  owner?: { email: string; full_name?: string };
  owner_id?: number;
  billing_owner?: { email: string; full_name?: string };
  billing_owner_id?: number;
  email_domain?: string;
  email?: string;
  enable_nexla_password_login?: boolean;
  org_tier?: string;
  org_tier_id?: number;
  trial_expires_at?: string | null;
  self_signup_members_limit?: number;
}

export interface UpdateOrgArg {
  id: number;
  body: UpdateOrgPayload;
}

export interface OrgMemberInput {
  id?: number;
  email?: string;
  full_name?: string;
  admin?: boolean;
  access_role?: OrgMemberAccessRole;
  delegate_owner_id?: number;
}

export interface UpdateOrgMembersArg {
  orgId: number;
  members: OrgMemberInput[];
}

export interface CreateOrgPayload {
  name: string;
  owner?: { full_name: string; email: string };
  owner_id?: number;
  billing_owner?: { full_name: string; email: string };
  billing_owner_id?: number;
  email_domain?: string;
  email?: string;
  org_tier_id?: number;
  members?: OrgMemberInput[];
}

export interface OrgAccountMetricsParams {
  orgId: number;
  from?: string;
  to?: string;
}

export interface UpdateOrgTierPayload {
  display_name?: string;
  record_count_limit?: number;
  record_count_limit_time?: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  data_source_count_limit?: number;
  user_members_limit?: number;
  trial_period_days?: number;
}

export interface UpdateOrgTierArg {
  id: number;
  body: UpdateOrgTierPayload;
}

export interface OrgAccountSummary {
  org_id: number;
  data_sources: {
    total: number;
    active: number;
    paused: number;
    initialized: number;
    rate_limited?: number;
  };
  data_sets?: Record<
    string,
    { total: number; active: number; paused: number; initialized: number }
  >;
  data_sinks?: {
    total: number;
    active: number;
    paused: number;
    initialized: number;
  };
}

export const ORGS_BASE_PATH = '/orgs';
export const ORG_TIERS_BASE_PATH = '/org_tiers';
